import React, { useEffect, useState } from 'react';
import { Container, Card, CardContent, Grid, Stack, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box } from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { formatDisplayDate } from '../utils/dateDisplay';

const API_BASE = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const Profile: React.FC = () => {
  const { state, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileName, setProfileName] = useState(state.user?.name || '');
  const [profilePhone, setProfilePhone] = useState(state.user?.phone || '');
  const [profileAddress, setProfileAddress] = useState(state.user?.address || '');
  const [profileState, setProfileState] = useState(state.user?.state || '');
  const [profileCountry, setProfileCountry] = useState(state.user?.country || '');
  const [billingName, setBillingName] = useState(state.user?.billingAddress?.name || '');
  const [billingAddressLine1, setBillingAddressLine1] = useState(state.user?.billingAddress?.addressLine1 || '');
  const [billingAddressLine2, setBillingAddressLine2] = useState(state.user?.billingAddress?.addressLine2 || '');
  const [billingCity, setBillingCity] = useState(state.user?.billingAddress?.city || '');
  const [billingState, setBillingState] = useState(state.user?.billingAddress?.state || '');
  const [billingCountry, setBillingCountry] = useState(state.user?.billingAddress?.country || '');
  const [billingPincode, setBillingPincode] = useState(state.user?.billingAddress?.pincode || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const subscription = state.user?.subscription;
  const isPremium = hasPremiumSubscription(state.user);

  const shouldShowUpgrade = Boolean(
    state.user &&
    state.user.role !== 'admin' &&
    !hasPremiumSubscription(state.user)
  );

  const formatDate = (value?: string) => {
    return formatDisplayDate(value, { fallback: '' });
  };

  useEffect(() => {
    setProfileName(state.user?.name || '');
    setProfilePhone(state.user?.phone || '');
    setProfileAddress(state.user?.address || '');
    setProfileState(state.user?.state || '');
    setProfileCountry(state.user?.country || '');
    setBillingName(state.user?.billingAddress?.name || '');
    setBillingAddressLine1(state.user?.billingAddress?.addressLine1 || '');
    setBillingAddressLine2(state.user?.billingAddress?.addressLine2 || '');
    setBillingCity(state.user?.billingAddress?.city || '');
    setBillingState(state.user?.billingAddress?.state || '');
    setBillingCountry(state.user?.billingAddress?.country || '');
    setBillingPincode(state.user?.billingAddress?.pincode || '');
  }, [state.user]);

  const handleSaveProfile = async () => {
    const name = profileName.trim();
    const profileUpdates: any = {
      name,
      phone: profilePhone.trim(),
      address: profileAddress.trim(),
      state: profileState.trim(),
      country: profileCountry.trim(),
    };

    if (isPremium) {
      profileUpdates.billingAddress = {
        name: billingName.trim(),
        addressLine1: billingAddressLine1.trim(),
        addressLine2: billingAddressLine2.trim(),
        city: billingCity.trim(),
        state: billingState.trim(),
        country: billingCountry.trim(),
        pincode: billingPincode.trim(),
      };
    }

    if (!name) {
      setProfileError('Name is required.');
      setProfileSuccess('');
      return;
    }

    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      await updateUser(profileUpdates);
      setProfileSuccess('Profile updated successfully.');
    } catch (err: any) {
      setProfileError(err.message || 'Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE exactly to confirm.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/users/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.token}`,
        },
        body: JSON.stringify({ confirmPhrase: 'DELETE MY ACCOUNT' }),
      });
      const json = await res.json();
      if (res.ok) {
        logout();
        navigate('/', { replace: true });
      } else {
        setError(json.message || 'Failed to delete account.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields.');
      setPasswordSuccess('');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      setPasswordSuccess('');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      setPasswordSuccess('');
      return;
    }

    setChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const res = await fetch(`${API_BASE}/users/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || json.error || 'Failed to change password.');
      }

      setPasswordSuccess(json.message || 'Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password.');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        प्रोफाइल / Profile
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>व्यक्तिगत जानकारी</Typography>
              <Typography><strong>नाम:</strong> {state.user?.name}</Typography>
              <Typography><strong>ईमेल:</strong> {state.user?.email}</Typography>
              {state.user?.phone && <Typography><strong>फोन:</strong> {state.user.phone}</Typography>}
              {state.user?.address && <Typography><strong>पता:</strong> {state.user.address}</Typography>}
              {state.user?.state && <Typography><strong>राज्य:</strong> {state.user.state}</Typography>}
              {state.user?.country && <Typography><strong>देश:</strong> {state.user.country}</Typography>}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>सब्स्क्रिप्शन</Typography>
              <Typography><strong>प्लान:</strong> {state.user?.subscription.type}</Typography>
              {subscription?.startDate && (
                <Typography><strong>शुरू:</strong> {formatDate(subscription.startDate)}</Typography>
              )}
              {subscription?.endDate && (
                <Typography><strong>Valid Until:</strong> {formatDate(subscription.endDate)}</Typography>
              )}
              <Typography><strong>चार्ट्स बचे:</strong> {hasPremiumSubscription(state.user) ? 'Unlimited' : state.user?.subscription.chartsRemaining}</Typography>
              {shouldShowUpgrade && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" component={RouterLink} to="/upgrade">
                    Upgrade Plan
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {isPremium && (
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Billing address</Typography>
              <Stack spacing={2} sx={{ mt: 2, maxWidth: 720 }}>
                <TextField
                  fullWidth
                  label="Billing name / legal name"
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  fullWidth
                  label="Address line 1"
                  value={billingAddressLine1}
                  onChange={(e) => setBillingAddressLine1(e.target.value)}
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 250 }}
                />
                <TextField
                  fullWidth
                  label="Address line 2 (optional)"
                  value={billingAddressLine2}
                  onChange={(e) => setBillingAddressLine2(e.target.value)}
                  inputProps={{ maxLength: 250 }}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="City"
                      value={billingCity}
                      onChange={(e) => setBillingCity(e.target.value)}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="State"
                      value={billingState}
                      onChange={(e) => setBillingState(e.target.value)}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={billingCountry}
                      onChange={(e) => setBillingCountry(e.target.value)}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="PIN / Postal code"
                      value={billingPincode}
                      onChange={(e) => setBillingPincode(e.target.value)}
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                </Grid>
                <Box>
                  <Button variant="contained" onClick={handleSaveProfile} disabled={savingProfile}>
                    {savingProfile ? 'Saving…' : 'Save Billing Address'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Profile Details</Typography>
              <Typography variant="body2" color="text.secondary">
                These details will be shown on the PDF you generate.
              </Typography>
              <Stack spacing={2} sx={{ mt: 2, maxWidth: 640 }}>
                {profileError && <Alert severity="error">{profileError}</Alert>}
                {profileSuccess && <Alert severity="success">{profileSuccess}</Alert>}
                <TextField
                  fullWidth
                  label="Name"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={profilePhone}
                  onChange={(e) => setProfilePhone(e.target.value)}
                  inputProps={{ maxLength: 20 }}
                />
                <TextField
                  fullWidth
                  label="Address"
                  value={profileAddress}
                  onChange={(e) => setProfileAddress(e.target.value)}
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 250 }}
                />
                <TextField
                  fullWidth
                  label="State"
                  value={profileState}
                  onChange={(e) => setProfileState(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  fullWidth
                  label="Country"
                  value={profileCountry}
                  onChange={(e) => setProfileCountry(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
                <Box>
                  <Button variant="contained" onClick={handleSaveProfile} disabled={savingProfile}>
                    {savingProfile ? 'Saving…' : 'Save Profile'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Change Password</Typography>
              <Stack spacing={2} sx={{ mt: 2, maxWidth: 520 }}>
                {passwordError && <Alert severity="error">{passwordError}</Alert>}
                {passwordSuccess && <Alert severity="success">{passwordSuccess}</Alert>}
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Box>
                  <Button variant="contained" onClick={handleChangePassword} disabled={changingPassword}>
                    {changingPassword ? 'Changing…' : 'Change Password'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Danger Zone */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Typography variant="h6" color="error" gutterBottom>Danger Zone</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete your account and anonymise all saved charts. This action cannot be undone.
              </Typography>
              <Button variant="outlined" color="error" onClick={() => setDialogOpen(true)}>
                Delete My Account
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setConfirmText(''); setError(''); }}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently delete your account and anonymise all birth charts you have saved.
            Your subscription history will be removed. <strong>This cannot be undone.</strong>
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Type <strong>DELETE</strong> to confirm:
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setConfirmText(''); setError(''); }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default Profile;
